import { Store } from "@reduxjs/toolkit";
import Keycloak, { KeycloakInitOptions } from "keycloak-js";

import {
  logout as logoutAction,
  signIn,
  updateAuthReady,
  updateNotAuthenticated,
} from "../slice";
import {
  AuthConfig,
  KeycloakSetupOptions,
  IAuthProvider,
} from "../interfaces/IAuthProvider";
import {
  KEYCLOAK_SESSION_ACTIVE_KEY,
  KEYCLOAK_TOKEN_KEY,
  KEYCLOAK_REFRESH_TOKEN_KEY,
  NETWORK_DISCONNECT_EVENT,
} from "../constants";
import { isSafari } from "react-device-detect";
import { isNetworkDisconnectError } from "../utils/isNetworkDisconnectError";

export async function exitPageFullscreenSafari() {
  if (!isSafari) return;
  try {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (doc.exitFullscreen) return await doc.exitFullscreen();
    if (doc.webkitExitFullscreen) return await doc.webkitExitFullscreen();
  } catch (e) {
    console.error("[exitPageFullscreen] failed:", e);
  }
}

export class KeycloakProvider implements IAuthProvider<KeycloakSetupOptions> {
  private config: AuthConfig;
  private keycloakInstance: Keycloak | null = null;
  private store: Store | null = null;

  constructor(config: AuthConfig) {
    this.config = config;
  }

  async setup(
    options: KeycloakSetupOptions,
  ): Promise<{ authenticated: boolean }> {
    this.store = options.store;
    const dispatch = this.store.dispatch;

    // Reuse existing instance
    if (this.keycloakInstance) {
      if (this.keycloakInstance.authenticated) dispatch(signIn());
      dispatch(updateAuthReady(true));
      return { authenticated: this.keycloakInstance.authenticated ?? false };
    }

    const wasSessionActive =
      localStorage.getItem(KEYCLOAK_SESSION_ACTIVE_KEY) === "true";
    const { silentCheckSsoRedirectUri = "/silent-check-sso.html", onLoad } =
      options;
    const urlOnLoad = new URLSearchParams(window.location.search).get(
      "onLoad",
    ) as "login-required" | "check-sso" | undefined;

    const onLoadStrategy =
      urlOnLoad ||
      onLoad ||
      options.onLoad ||
      (!wasSessionActive ? "login-required" : "check-sso");

    localStorage.setItem(KEYCLOAK_SESSION_ACTIVE_KEY, "true");

    // Restore previously saved tokens to bypass the check-sso iframe.
    // This avoids third-party cookie issues (requestStorageAccess denied)
    // that cause check-sso to fail on reload for certain auth flows (e.g. phone OTP).
    const savedToken = localStorage.getItem(KEYCLOAK_TOKEN_KEY) ?? undefined;
    const savedRefreshToken =
      localStorage.getItem(KEYCLOAK_REFRESH_TOKEN_KEY) ?? undefined;

    const keycloakConfig = {
      url: this.config.serverUrl,
      realm: this.config.realm,
      clientId: this.config.clientId,
    };
    this.keycloakInstance = new Keycloak(keycloakConfig);

    try {
      if (!window.navigator.onLine) throw new Error("No internet connection");

      const authenticated = await this.initWithTimeout({
        onLoad: onLoadStrategy,
        ...(onLoadStrategy === "check-sso" && {
          silentCheckSsoRedirectUri:
            window.location.origin + silentCheckSsoRedirectUri,
          silentCheckSsoFallback: true,
        }),
        token: savedToken,
        refreshToken: savedRefreshToken,
        checkLoginIframe: false,
        enableLogging: true,
        pkceMethod: "S256",
      });

      if (authenticated) {
        this.saveTokens();
        dispatch(signIn());
      } else {
        dispatch(updateNotAuthenticated());
      }

      return { authenticated };
    } catch (error) {
      // Timeout typically means Safari ITP blocked the check-sso iframe →
      // init hangs. Force a fresh login redirect so the user gets back via the
      // Keycloak callback flow (which doesn't go through the blocked iframe).
      if (
        error instanceof Error &&
        error.message.startsWith("Keycloak init timed out")
      ) {
        if (savedToken && savedRefreshToken) {
          await this.login();
          return { authenticated: true };
        }
      }
      if (isNetworkDisconnectError(error)) {
        window.dispatchEvent(new CustomEvent(NETWORK_DISCONNECT_EVENT));
      }
      console.log("Failed to initialize Keycloak", error);
      return { authenticated: false };
    } finally {
      dispatch(updateAuthReady(true));
    }
  }

  private async initWithTimeout(
    initOptions: KeycloakInitOptions,
    timeoutMs = 5000,
  ): Promise<boolean> {
    if (!this.keycloakInstance) return false;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`Keycloak init timed out after ${timeoutMs}ms`)),
        timeoutMs,
      );
    });

    try {
      return await Promise.race([
        this.keycloakInstance.init(initOptions),
        timeout,
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  private saveTokens(): void {
    if (this.keycloakInstance?.token) {
      localStorage.setItem(KEYCLOAK_TOKEN_KEY, this.keycloakInstance.token);
    }
    if (this.keycloakInstance?.refreshToken) {
      localStorage.setItem(
        KEYCLOAK_REFRESH_TOKEN_KEY,
        this.keycloakInstance.refreshToken,
      );
    }
  }

  async login(redirectUri?: string): Promise<void> {
    try {
      await exitPageFullscreenSafari();
      await this.keycloakInstance?.login({
        redirectUri: redirectUri || window.location.href,
      });
    } catch (error) {
      const errorMessage =
        typeof error === "string" ? error : (error as Error).message;
      console.log("Failed to login with Keycloak", errorMessage);
    }
  }

  async logout(ignoreDispatch = false): Promise<void> {
    try {
      await exitPageFullscreenSafari();
      localStorage.removeItem(KEYCLOAK_SESSION_ACTIVE_KEY);
      localStorage.removeItem(KEYCLOAK_TOKEN_KEY);
      localStorage.removeItem(KEYCLOAK_REFRESH_TOKEN_KEY);
      if (this.keycloakInstance) {
        // get the root url without query params
        const rootUrl = window.location.origin.split("?")[0];
        await this.keycloakInstance.logout({
          redirectUri: rootUrl,
        });
        this.keycloakInstance = null;
        if (!ignoreDispatch && this.store) {
          this.store.dispatch(logoutAction());
        }
      }
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  async getToken(): Promise<string | undefined> {
    try {
      if (this.keycloakInstance) {
        const refreshed = await this.keycloakInstance.updateToken(30);
        if (refreshed) this.saveTokens();
        return this.keycloakInstance.token;
      }
    } catch (error) {
      console.log(error);
    }
    return undefined;
  }

  getCurrentToken(): string | undefined {
    return this.keycloakInstance?.token;
  }

  getClientId(): string {
    return this.config.clientId;
  }

  isInternalAccount(): boolean {
    const roles = this.keycloakInstance?.tokenParsed?.realm_access?.roles || [];
    const hasAccess = roles.some((role) => ["QA", "ADMIN"].includes(role));
    return hasAccess;
  }
}
